import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useCounterPartyController } from 'api/controllers/CounterPartyController';

import { CounterpartyCreateUpdate, CounterpartyType, Invoice } from 'openapi';

export const useInlineCounterpartyCreate = (
  currentInvoice: Invoice,
  initialName = '',
  onSuccess?: (name: string) => void
) => {
  const { setValue, clearErrors } = useFormContext();
  const { createNewCounterParty } = useCounterPartyController();

  const [isCreating, setIsCreating] = useState(false);
  const [cardName, setCardName] = useState(initialName);
  const [cardVat, setCardVat] = useState('');
  const [cardReg, setCardReg] = useState('');

  const resetCard = useCallback(() => {
    setCardName('');
    setCardVat('');
    setCardReg('');
  }, []);

  const handleCreate = useCallback(async () => {
    const trimmedName = cardName.trim();
    const trimmedVat = cardVat.trim();
    const trimmedReg = cardReg.trim();

    const createBody: CounterpartyCreateUpdate = {
      name: trimmedName,
      vatNumber: trimmedVat,
      registrationNumber: trimmedReg,
      ibans: [],
      type: CounterpartyType.SUPPLIER
    };

    setIsCreating(true);

    try {
      const createdCounterparty = await createNewCounterParty(
        Number(currentInvoice.companyId),
        createBody
      );

      const resolvedCounterpartyName = createdCounterparty.name || trimmedName;

      setValue('counterpartyId', createdCounterparty.id ?? null);
      setValue('counterpartyName', resolvedCounterpartyName);
      setValue(
        'counterpartyVatNumber',
        createdCounterparty.vatNumber || trimmedVat
      );
      setValue(
        'counterpartyRegistrationNumber',
        createdCounterparty.registrationNumber || trimmedReg
      );
      setValue(
        'isCounterpartyValidated',
        createdCounterparty.isValidated ?? false
      );
      clearErrors();

      onSuccess?.(resolvedCounterpartyName);
    } finally {
      setIsCreating(false);
    }
  }, [
    cardName,
    cardVat,
    cardReg,
    clearErrors,
    createNewCounterParty,
    currentInvoice.companyId,
    onSuccess,
    setValue
  ]);

  return {
    cardName,
    setCardName,
    cardVat,
    setCardVat,
    cardReg,
    setCardReg,
    isCreating,
    resetCard,
    handleCreate
  };
};
